/**
 * Class Code Generator
 * Auto-generates enrollment codes or allows custom input
 */

import { useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RefreshCw, Copy, Check } from 'lucide-react';

interface ClassCodeGeneratorProps {
  classCode: string;
  onClassCodeChange: (code: string) => void;
}

/**
 * Generate a random class code (8 chars, no ambiguous characters)
 * Uses CLASS_CODE_CHARS: ABCDEFGHJKLMNPQRSTUVWXYZ23456789
 */
function generateClassCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export function ClassCodeGenerator({
  classCode,
  onClassCodeChange,
}: ClassCodeGeneratorProps) {
  const [copied, setCopied] = useState(false);

  const handleGenerate = () => {
    const newCode = generateClassCode();
    onClassCodeChange(newCode);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(classCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="classCode">
        Class Code
        <span className="text-muted-foreground text-sm ml-2">
          (Students use this to enroll)
        </span>
      </Label>
      <div className="flex gap-2">
        <Input
          id="classCode"
          value={classCode}
          onChange={(e) => onClassCodeChange(e.target.value.toUpperCase())}
          placeholder="AUTO-GENERATED"
          className="flex-1 font-mono"
          maxLength={12}
        />
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={handleGenerate}
          title="Generate new code"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
        {classCode && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleCopy}
            title="Copy code"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-600" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Leave empty to auto-generate, or enter a custom code (6-12 uppercase alphanumeric
        characters)
      </p>
    </div>
  );
}
